import React from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import styles from './Button.module.scss';
import Spinner from '../../../common/components/Spinner';

export const CustomButton = ({
  children,
  onClick = () => { },
  type,
  disabled,
  customStyles,
  variant = 'primary',
  label,
  className,
  size,
  mini,
  disabledClassName,
  loading,
  loadingSpinner = false,
  loadingText,
  onMouseLeave,
  onMouseOver,
  onMouseDown,
  onMouseUp,
  ignoreDisabled,
  ariaLabel = null,
  tabIndex,
  hide = false,
  centerContent = false
}) => {
  if (hide) {
    return null;
  }

  const renderChildren = () => {
    if (loading) {
      if (loadingSpinner) {
        return (
          <>
            <Spinner width="20px" height="20px" size="3px" />
            {loadingText && <div className={styles.spinnerText}>{loadingText}</div>}
          </>
        );
      }
      return loadingText;
    }

    if (label) {
      return label;
    }
    if (children) {
      return children;
    }
    return 'Button';
  };

  const handleClick = event => {
    // We should use this approach instead of adding the disabled attribute directly to the button tag
    // because in some cases we want to detect the onMouseLeave action of the disabled button
    // to hide the hoverText, which the disabled attribute does not provide.
    if (!disabled && !loading) {
      onClick(event);
    }
  };

  const _className = cx(className, styles[size], styles.button, styles[variant], {
    [styles.disabled]: disabled || loading,
    [disabledClassName]: disabled,
    [styles.mini]: mini,
    [styles.centerContent]: centerContent,
  });

  return (
    <button
      type={type}
      className={_className}
      style={customStyles}
      onClick={handleClick}
      disabled={!ignoreDisabled && (disabled || loading)} // in some cases disabled is necessary
      onMouseLeave={onMouseLeave}
      onMouseOver={onMouseOver}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      {...(ariaLabel && { "aria-label": ariaLabel })}
      {...(tabIndex !== undefined && { tabIndex })}
    >
      {renderChildren()}
    </button>
  );
};

CustomButton.propTypes = {
  onClick: PropTypes.func,
  children: PropTypes.node,
  variant: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  size: PropTypes.string,
  disabledClassName: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  loadingText: PropTypes.string,
  onMouseOver: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onMouseDown: PropTypes.func,
  onMouseUp: PropTypes.func,
  hide: PropTypes.bool,
  centerContent: PropTypes.bool,
};

CustomButton.defaultProps = {
  className: '',
  label: '',
  size: 'normal',
  variant: '',
  disabled: false,
  disabledClassName: '',
  loading: false,
  loadingText: 'Loading...',
  hide: false,
  centerContent: false,
};
